import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  MultiSelect,
  RelativeDateInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StringSelect,
  cn,
} from "@tellescope/ui";
import type { DynamicDateValue, RelativeDateValue } from "@tellescope/ui";
import { Trash2 } from "lucide-react";
import type { LeafCondition, DataSourceType } from "../../../shared/schema/visibility";
import { isComparisonOperator } from "../../../shared/schema/visibility";
import { useAuth } from "@tellescope/tanstack-query";
import {
  getDataSources,
  buildCustomEnduserFieldDefinitions,
  getFieldsForSource,
  getOperatorsForField,
  getBaseFieldId,
  getSuffixKey,
  createFieldIdWithSuffix,
  type FieldDefinition,
} from "./constants";

/**
 * Creates the default relative date value used when initializing date fields.
 */
function createDefaultRelativeDateValue(): RelativeDateValue {
  return {
    $relative: {
      amount: 30,
      unit: "days",
      direction: "ago",
    },
  };
}

interface ConditionRuleProps {
  condition: LeafCondition;
  onChange: (updates: Partial<LeafCondition>) => void;
  onRemove: () => void;
  className?: string;
}

/**
 * Extracts the operator ID from a value.
 * Returns the operator key (e.g., "$eq", "$in").
 * Primitive values are treated as implicit $eq for UI display purposes.
 */
function getOperatorFromValue(value: LeafCondition["value"]): string {
  if (value === null || typeof value !== "object") {
    return "$eq"; // Direct value comparison is implicit $eq - show as "equals" in UI
  }
  const keys = Object.keys(value);
  return keys[0] ?? "$eq";
}

/**
 * Extracts the actual comparison value from the value field.
 * For date operators, this returns the dynamic date value directly.
 */
function getValueFromCondition(value: LeafCondition["value"]): string | number | boolean | null | DynamicDateValue {
  if (value === null || typeof value !== "object") {
    return value;
  }
  // Use type guard instead of cast
  if (!isComparisonOperator(value)) {
    return "";
  }
  if ("$eq" in value) return value.$eq;
  if ("$ne" in value) return value.$ne;
  // Date operators may contain DynamicDateValue (string, relative object, or number)
  if ("$gt" in value) return value.$gt;
  if ("$gte" in value) return value.$gte;
  if ("$lt" in value) return value.$lt;
  if ("$lte" in value) return value.$lte;
  if ("$exists" in value) return value.$exists;
  if ("$regex" in value) return value.$regex;
  if ("$in" in value) return value.$in.join(", ");
  if ("$nin" in value) return value.$nin.join(", ");
  return "";
}

/**
 * Checks if a value is a DynamicDateValue (for date operators).
 */
function isDynamicDateValue(value: unknown): value is DynamicDateValue {
  if (value === "$now" || value === "$today") return true;
  if (typeof value === "object" && value !== null && "$relative" in value) return true;
  return false;
}

/**
 * Extracts the date value from a condition for the RelativeDateInput component.
 */
function extractDateValue(value: LeafCondition["value"]): DynamicDateValue | number | null {
  const extracted = getValueFromCondition(value);
  if (isDynamicDateValue(extracted)) return extracted;
  if (typeof extracted === "number") return extracted;
  return null;
}

/**
 * Creates a value object with the specified operator.
 */
function createValueWithOperator(
  operator: string,
  rawValue: string | number | boolean | null | DynamicDateValue,
  valueType?: string
): LeafCondition["value"] {
  // Direct comparison - use primitive value (implicit $eq)
  // This keeps the schema cleaner and matches how conditions are typically stored
  if (operator === "$eq") {
    if (valueType === "number" && typeof rawValue === "string") {
      return Number(rawValue) as LeafCondition["value"];
    }
    return rawValue as LeafCondition["value"];
  }

  // Boolean operators
  if (operator === "$exists") {
    return { $exists: rawValue === true || rawValue === "true" };
  }

  // Array operators (parse comma-separated values)
  if (operator === "$in" || operator === "$nin") {
    const values =
      typeof rawValue === "string"
        ? rawValue
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : [rawValue as string | number | boolean | null];
    return operator === "$in" ? { $in: values } : { $nin: values };
  }

  // Numeric/Date operators - handle both numbers and DynamicDateValue
  if (["$gt", "$gte", "$lt", "$lte"].includes(operator)) {
    // For date fields, rawValue may be a DynamicDateValue
    if (valueType === "date") {
      // Use provided DynamicDateValue or create default if empty/invalid
      const dateValue = isDynamicDateValue(rawValue) ? rawValue : createDefaultRelativeDateValue();
      switch (operator) {
        case "$gt":
          return { $gt: dateValue };
        case "$gte":
          return { $gte: dateValue };
        case "$lt":
          return { $lt: dateValue };
        case "$lte":
          return { $lte: dateValue };
      }
    }

    // For numeric fields or non-date values, parse as number
    const numValue = valueType === "number" ? Number(rawValue) : rawValue;
    switch (operator) {
      case "$gt":
        return { $gt: numValue as number };
      case "$gte":
        return { $gte: numValue as number };
      case "$lt":
        return { $lt: numValue as number };
      case "$lte":
        return { $lte: numValue as number };
    }
  }

  // String operators
  switch (operator) {
    case "$ne":
      return {
        $ne:
          valueType === "number" && typeof rawValue === "string"
            ? Number(rawValue)
            : (rawValue as string | number | boolean | null),
      };
    case "$regex":
      return { $regex: rawValue as string };
  }

  return rawValue as LeafCondition["value"];
}

/**
 * A single condition rule (leaf condition) editor.
 * Allows selecting source, field, operator, and value.
 */
export function ConditionRule({ condition, onChange, onRemove, className }: ConditionRuleProps) {
  const { t } = useTranslation();
  const { selectedOrganization } = useAuth();

  // Get available fields for the current source, merging in custom enduser fields when applicable
  const fields = useMemo((): FieldDefinition[] => {
    const base = getFieldsForSource(condition.source);
    if (condition.source !== "enduser") return base;
    const customFields = selectedOrganization?.settings?.endusers?.customFields ?? [];
    if (customFields.length === 0) return base;
    return [...base, ...buildCustomEnduserFieldDefinitions(customFields)];
  }, [condition.source, selectedOrganization?.settings?.endusers?.customFields]);

  // Get the field definition — search the merged fields list so custom enduser fields are found
  const fieldDef = useMemo(() => {
    const exact = fields.find((f) => f.id === condition.field);
    if (exact) return exact;
    // Check suffix key fields (e.g., `productMetadata.tier` matches `productMetadata`)
    const baseId = getBaseFieldId(condition.field);
    if (baseId !== condition.field) {
      const base = fields.find((f) => f.id === baseId);
      if (base?.suffixKeyInput) return base;
    }
    return undefined;
  }, [fields, condition.field]);

  // Extract base field ID and suffix key for fields that support suffixKeyInput
  const baseFieldId = getBaseFieldId(condition.field);
  const suffixKey = getSuffixKey(condition.field);

  // Only use baseFieldId for fields with suffixKeyInput (e.g., productMetadata.tier -> productMetadata)
  // For regular nested fields like insurance.payerName, use the full field ID
  const displayFieldId = fieldDef?.suffixKeyInput ? baseFieldId : condition.field;

  // Get available operators for this field (respects field-level restrictions)
  const operators = useMemo(() => getOperatorsForField(fieldDef), [fieldDef]);

  // Get current operator and value
  const currentOperator = getOperatorFromValue(condition.value);
  const currentValue = getValueFromCondition(condition.value);

  // Get the operator definition
  const operatorDef = operators.find((op) => op.id === currentOperator);

  // Handle source change - reset field, operator, and value
  const handleSourceChange = useCallback(
    (newSource: DataSourceType) => {
      // Use the merged fields for enduser, static fields for all other sources
      const newFields = getFieldsForSource(newSource);
      const firstField = newFields[0];

      if (firstField) {
        const newOperators = getOperatorsForField(firstField);
        const firstOp = newOperators[0];
        // Use first option value if field has predefined options, otherwise empty string
        const defaultValue = firstField.options?.[0]?.value ?? "";
        const newValue = firstOp ? createValueWithOperator(firstOp.id, defaultValue, firstOp.valueType) : "";

        onChange({ source: newSource, field: firstField.id, value: newValue });
      } else {
        onChange({ source: newSource, field: "", value: "" });
      }
    },
    [onChange]
  );

  // Handle field change
  const handleFieldChange = useCallback(
    (newField: string) => {
      // Look up the field definition from the merged fields list
      const newFieldDef = fields.find((f) => f.id === newField);
      const newOperators = getOperatorsForField(newFieldDef);

      // Reset to first operator when field changes
      // Use first option value if field has predefined options, otherwise empty string
      const firstOp = newOperators[0];
      const defaultValue = newFieldDef?.options?.[0]?.value ?? "";
      const newValue = firstOp ? createValueWithOperator(firstOp.id, defaultValue, firstOp.valueType) : "";

      // If the new field requires a suffix key, use the base field ID
      // (the suffix key will be empty until the user enters it)
      onChange({ field: newField, value: newValue });
    },
    [fields, onChange]
  );

  // Handle suffix key change (for fields with suffixKeyInput)
  const handleSuffixKeyChange = useCallback(
    (newSuffixKey: string) => {
      // Remove any dots from the suffix key input (they should only type the key name)
      const cleanKey = newSuffixKey.replace(/\./g, "");
      const newFieldId = createFieldIdWithSuffix(baseFieldId, cleanKey);
      onChange({ field: newFieldId });
    },
    [baseFieldId, onChange]
  );

  // Handle operator change
  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const opDef = operators.find((op) => op.id === newOperator);
      // Preserve the current value but wrap it in the new operator
      const newValue = createValueWithOperator(newOperator, currentValue as string, opDef?.valueType);
      onChange({ value: newValue });
    },
    [operators, currentValue, onChange]
  );

  // Handle value change
  const handleValueChange = useCallback(
    (newRawValue: string) => {
      const newValue = createValueWithOperator(currentOperator, newRawValue, operatorDef?.valueType);
      onChange({ value: newValue });
    },
    [currentOperator, operatorDef?.valueType, onChange]
  );

  // Handle multi-value change (for $in/$nin multiselect)
  const handleMultiValueChange = useCallback(
    (values: string[]) => {
      const coerced = fieldDef?.type === "number" ? values.map(Number) : values;
      const newValue = currentOperator === "$in" ? { $in: coerced } : { $nin: coerced };
      onChange({ value: newValue });
    },
    [currentOperator, fieldDef?.type, onChange]
  );

  // Extract current values as array for multiselect
  const currentMultiValues = useMemo((): string[] => {
    if (typeof condition.value === "object" && condition.value !== null) {
      if ("$in" in condition.value) return condition.value.$in.map(String);
      if ("$nin" in condition.value) return condition.value.$nin.map(String);
    }
    return [];
  }, [condition.value]);

  // Handle boolean value change
  const handleBooleanChange = useCallback(
    (boolValue: string) => {
      const newValue = createValueWithOperator(currentOperator, boolValue === "true", operatorDef?.valueType);
      onChange({ value: newValue });
    },
    [currentOperator, operatorDef?.valueType, onChange]
  );

  // Handle date value change (for RelativeDateInput)
  const handleDateValueChange = useCallback(
    (dateValue: DynamicDateValue | number) => {
      const newValue = createValueWithOperator(currentOperator, dateValue, "date");
      onChange({ value: newValue });
    },
    [currentOperator, onChange]
  );

  return (
    <div className={cn("flex items-center gap-2 p-2 bg-muted/30 rounded-md", className)}>
      {/* Source selector - always shown to make future extensibility visible */}
      <StringSelect
        value={condition.source}
        onValueChange={handleSourceChange}
        options={getDataSources().map((source) => ({ value: source.id, label: source.label }))}
        placeholder="Source"
        className="w-[120px] h-8 text-xs"
      />

      {/* Field selector - use displayFieldId to handle both suffix key fields and regular nested fields */}
      <Select value={displayFieldId} onValueChange={handleFieldChange}>
        <SelectTrigger className="w-[160px] h-8 text-xs">
          <SelectValue placeholder="Select field" />
        </SelectTrigger>
        <SelectContent position="item-aligned">
          {fields.map((field) => (
            <SelectItem key={field.id} value={field.id} className="text-xs">
              {field.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Suffix key input - shown when field requires a key (e.g., productMetadata.tier) */}
      {fieldDef?.suffixKeyInput && (
        <Input
          type="text"
          value={suffixKey}
          onChange={(e) => handleSuffixKeyChange(e.target.value)}
          placeholder={fieldDef.suffixKeyInput.placeholder}
          className="w-[140px] h-8 text-xs"
        />
      )}

      {/* Operator selector */}
      <Select value={currentOperator} onValueChange={handleOperatorChange}>
        <SelectTrigger className="w-[140px] h-8 text-xs">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op.id} value={op.id} className="text-xs">
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value input — operator type always takes precedence over field options.
          For fields with predefined options, those options seed the select/multiselect
          inputs but only when the operator actually calls for that kind of input. */}
      {operatorDef?.valueType === "boolean" ? (
        <Select value={String(currentValue)} onValueChange={handleBooleanChange}>
          <SelectTrigger className="w-[100px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true" className="text-xs">
              {t("Yes")}
            </SelectItem>
            <SelectItem value="false" className="text-xs">
              {t("No")}
            </SelectItem>
          </SelectContent>
        </Select>
      ) : operatorDef?.valueType === "date" ? (
        <RelativeDateInput
          value={extractDateValue(condition.value)}
          onChange={handleDateValueChange}
          className="flex-1 min-w-[200px]"
        />
      ) : operatorDef?.valueType === "multiselect" ? (
        // For fields with predefined options (e.g. Select, Multiple Select), seed the
        // multiselect with those options so the user can pick from them. Free-form
        // entry is still allowed for fields without options.
        <MultiSelect
          options={fieldDef?.options ? fieldDef.options : currentMultiValues.map((v) => ({ value: v, label: v }))}
          value={currentMultiValues}
          onValueChange={handleMultiValueChange}
          placeholder={t("Add values...")}
          className="flex-1 min-w-0 max-h-16 overflow-y-auto text-xs"
          maxDisplayCount={1}
          creator={
            fieldDef?.options
              ? undefined
              : {
                  render: (inputValue) => <span className="text-xs">{t('Add "{{inputValue}}"', { inputValue })}</span>,
                  transform: (inputValue) => inputValue.trim(),
                  onCreate: () => {},
                }
          }
        />
      ) : fieldDef?.options ? (
        // Single-value select for fields with predefined options (e.g. $eq/$ne on Select fields)
        <Select value={String(currentValue ?? "")} onValueChange={handleValueChange}>
          <SelectTrigger className="flex-1 h-8 text-xs min-w-[120px]">
            <SelectValue placeholder={t("Select value")} />
          </SelectTrigger>
          <SelectContent>
            {fieldDef.options.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-xs">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          type={operatorDef?.valueType === "number" ? "number" : "text"}
          value={String(currentValue ?? "")}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={t("Value")}
          className="flex-1 h-8 text-xs min-w-[120px]"
        />
      )}

      {/* Remove button */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
